import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  alpha,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { PageLayout, PageSection, GlassCard } from '@paneon/ui';
import { useTheme } from '@paneon/theme';
import { useTodayFeedings } from '../hooks/useFeedings';
import { usePets } from '../hooks/usePets';
import { AddFeedingDialog, type FeedingFormData } from '../components/AddFeedingDialog';
import type { Feeding } from '../services/api';

const feedingTypeLabels: Record<string, { label: string; emoji: string }> = {
  breakfast: { label: 'Frühstück', emoji: '🌅' },
  lunch: { label: 'Mittag', emoji: '☀️' },
  dinner: { label: 'Abend', emoji: '🌙' },
  snack: { label: 'Snack', emoji: '🍪' },
};

const foodTypeLabels: Record<string, string> = {
  dry: 'Trockenfutter',
  wet: 'Nassfutter',
  raw: 'BARF',
  homemade: 'Selbstgekocht',
  treat: 'Leckerli',
};

function formatTime(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function FeedPage() {
  const { isDarkMode } = useTheme();
  const { feedings, loading, error, addFeeding, deleteFeeding } = useTodayFeedings();
  const { pets } = usePets();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const handleAdd = () => {
    setDialogOpen(true);
  };

  const handleDelete = async (feeding: Feeding) => {
    try {
      await deleteFeeding(feeding.id);
      setSnackbar({
        open: true,
        message: 'Fütterung gelöscht',
        severity: 'success',
      });
    } catch {
      setSnackbar({
        open: true,
        message: 'Fehler beim Löschen',
        severity: 'error',
      });
    }
  };

  const handleSave = async (data: FeedingFormData) => {
    try {
      await addFeeding(data);
      setSnackbar({
        open: true,
        message: 'Fütterung eingetragen',
        severity: 'success',
      });
    } catch {
      setSnackbar({
        open: true,
        message: 'Fehler beim Speichern',
        severity: 'error',
      });
      throw new Error('Save failed');
    }
  };

  // Group feedings by pet
  const feedingsByPet = feedings.reduce(
    (acc, feeding) => {
      const petName = feeding.pet_name || 'Unbekannt';
      if (!acc[petName]) acc[petName] = [];
      acc[petName].push(feeding);
      return acc;
    },
    {} as Record<string, Feeding[]>
  );

  // Loading state
  if (loading) {
    return (
      <PageLayout title="Fütterung">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 200,
          }}
        >
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <PageLayout title="Fütterung">
        <PageSection>
          <GlassCard sx={{ p: 3 }}>
            <Typography color="error">{error}</Typography>
          </GlassCard>
        </PageSection>
      </PageLayout>
    );
  }

  return (
    <>
      <PageLayout
        title="Fütterung"
        rightAction={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            sx={{ borderRadius: 2, textTransform: 'none' }}
            onClick={handleAdd}
            disabled={pets.length === 0}
          >
            Eintragen
          </Button>
        }
      >
        <PageSection title="Heute">
          {pets.length === 0 ? (
            <GlassCard
              sx={{
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 150,
              }}
            >
              <Box sx={{ fontSize: 48, mb: 2 }}>🐾</Box>
              <Typography
                variant="body1"
                sx={{
                  color: isDarkMode ? alpha('#fff', 0.6) : alpha('#000', 0.6),
                  mb: 2,
                  textAlign: 'center',
                }}
              >
                Füge zuerst ein Haustier hinzu
              </Typography>
            </GlassCard>
          ) : feedings.length === 0 ? (
            <GlassCard
              sx={{
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 150,
              }}
            >
              <Box sx={{ fontSize: 48, mb: 2 }}>🍖</Box>
              <Typography
                variant="body1"
                sx={{
                  color: isDarkMode ? alpha('#fff', 0.6) : alpha('#000', 0.6),
                  mb: 2,
                  textAlign: 'center',
                }}
              >
                Keine Fütterungen für heute eingetragen
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                sx={{ borderRadius: 2, textTransform: 'none' }}
                onClick={handleAdd}
              >
                Fütterung eintragen
              </Button>
            </GlassCard>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {Object.entries(feedingsByPet).map(([petName, petFeedings]) => (
                <GlassCard key={petName} sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: isDarkMode ? '#fff' : '#000',
                      mb: 1.5,
                    }}
                  >
                    {petName}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {petFeedings.map(feeding => {
                      const type = feedingTypeLabels[feeding.feeding_type] || {
                        label: feeding.feeding_type,
                        emoji: '🍽️',
                      };
                      return (
                        <Box
                          key={feeding.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1,
                            borderRadius: 1,
                            backgroundColor: isDarkMode ? alpha('#fff', 0.05) : alpha('#000', 0.03),
                          }}
                        >
                          <Typography sx={{ fontSize: 20 }}>{type.emoji}</Typography>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: isDarkMode ? '#fff' : '#000',
                              }}
                            >
                              {type.label}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              <Chip
                                label={foodTypeLabels[feeding.food_type] || feeding.food_type}
                                size="small"
                                sx={{ height: 18, fontSize: 10 }}
                              />
                              {feeding.amount_grams && (
                                <Chip
                                  label={`${feeding.amount_grams}g`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: 10 }}
                                />
                              )}
                            </Box>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDarkMode ? alpha('#fff', 0.5) : alpha('#000', 0.5),
                            }}
                          >
                            {formatTime(feeding.fed_at)}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(feeding)}
                            sx={{
                              color: isDarkMode ? alpha('#fff', 0.4) : alpha('#000', 0.4),
                              '&:hover': { color: '#f44336' },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      );
                    })}
                  </Box>
                </GlassCard>
              ))}
            </Box>
          )}
        </PageSection>

        <PageSection title="Übersicht">
          <GlassCard
            sx={{
              p: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#4CAF50',
                }}
              >
                {feedings.length}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: isDarkMode ? alpha('#fff', 0.6) : alpha('#000', 0.6),
                }}
              >
                Fütterungen heute
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: isDarkMode ? '#fff' : '#000',
                }}
              >
                {feedings.reduce((sum, f) => sum + (f.amount_grams || 0), 0)}g
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: isDarkMode ? alpha('#fff', 0.6) : alpha('#000', 0.6),
                }}
              >
                Gesamtmenge
              </Typography>
            </Box>
          </GlassCard>
        </PageSection>
      </PageLayout>

      <AddFeedingDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        pets={pets}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
